/**
 * PR-Audit-02B G-03 (2026-06-19) — Table editor modal.
 *
 * Mirrors the RoomEditorModal pattern. Reuses existing dialog chrome,
 * existing API client (`api.ownerCreateTable` / `ownerUpdateTable` /
 * `ownerDeleteTable`). No new endpoints, no new dependencies.
 *
 * Modes:
 *   * mode="create" → POST /api/owner/brands/{brand_id}/rooms/{room_id}/tables
 *   * mode="edit"   → PATCH /api/owner/brands/{brand_id}/tables/{table_id}
 *   * mode="delete" → DELETE /api/owner/brands/{brand_id}/tables/{table_id} (confirm)
 *
 * "Move Table" already shipped (Phase E1 widened path + E3 P2 sort).
 * NOT re-implemented here.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';

export default function TableEditorModal({
  open,
  mode = 'create',
  branchId,
  roomId,                  // required for create
  table,                   // required for edit/delete
  onClose,
  onSuccess,
}) {
  const { token, user } = useAuthStore();
  const brandId = user?.brand_id;
  const qc = useQueryClient();

  const [form, setForm] = useState({ name: '', capacity: 4 });
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && table) {
      setForm({ name: table.name || '', capacity: table.capacity || 4 });
    } else {
      setForm({ name: '', capacity: 4 });
    }
    setError('');
  }, [open, mode, table]);

  const invalidateAfter = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: qk.ownerFloorPlanState(branchId) }),
      qc.invalidateQueries({ queryKey: ['room-tables', roomId] }),
    ]);
  };

  const createMutation = useMutation({
    mutationFn: () =>
      api.ownerCreateTable(token, brandId, roomId, {
        name: form.name.trim(),
        capacity: Number(form.capacity),
      }),
    onSuccess: async () => {
      await invalidateAfter();
      onSuccess?.();
      onClose?.();
    },
    onError: (e) => setError(e?.body?.detail || e?.message || 'Create failed'),
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      api.ownerUpdateTable(token, brandId, table.id, {
        name: form.name.trim(),
        capacity: Number(form.capacity),
      }),
    onSuccess: async () => {
      await invalidateAfter();
      onSuccess?.();
      onClose?.();
    },
    onError: (e) => setError(e?.body?.detail || e?.message || 'Update failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.ownerDeleteTable(token, brandId, table.id),
    onSuccess: async () => {
      await invalidateAfter();
      onSuccess?.();
      onClose?.();
    },
    onError: (e) => setError(e?.body?.detail || e?.message || 'Delete failed'),
  });

  if (!open) return null;
  const busy =
    createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;
  const title =
    mode === 'create' ? 'Add table'
    : mode === 'edit' ? `Edit table: ${table?.name || ''}`
    : `Delete table: ${table?.name || ''}`;

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    setError('');
    if (mode === 'create') return createMutation.mutate();
    if (mode === 'edit') return updateMutation.mutate();
    if (mode === 'delete') return deleteMutation.mutate();
  };

  return (
    <div
      data-testid="table-editor-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md rounded-2xl bg-[#15151B] border border-white/10 p-6 space-y-4"
        data-testid={`table-editor-${mode}`}
      >
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold text-white">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="table-editor-close"
            className="inline-flex items-center justify-center w-9 h-9 rounded-2xl text-white/70 hover:bg-white/5 hover:text-white"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {mode === 'delete' ? (
          <p className="text-sm text-white/70" data-testid="table-editor-delete-confirm">
            This will deactivate <span className="text-gold">{table?.name}</span>.
            Reservations referencing this table remain intact.
          </p>
        ) : (
          <>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/55">Name / label</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                maxLength={20}
                data-testid="table-editor-name"
                className="mt-1 w-full px-3 py-2 rounded-2xl bg-[#0E0E14] border border-white/10 text-sm text-white focus:outline-none focus:ring-2 focus:ring-gold/60"
                placeholder="e.g. T-01, Table 5"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/55">Capacity (pax)</label>
              <input
                type="number"
                min={1}
                max={50}
                value={form.capacity}
                onChange={(e) => setForm({ ...form, capacity: e.target.value })}
                required
                data-testid="table-editor-capacity"
                className="mt-1 w-full px-3 py-2 rounded-2xl bg-[#0E0E14] border border-white/10 text-sm text-white"
              />
            </div>
          </>
        )}

        {error ? (
          <div
            data-testid="table-editor-error"
            className="text-sm text-red-300 bg-red-900/20 border border-red-700/30 rounded-2xl px-3 py-2"
          >
            {error}
          </div>
        ) : null}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="table-editor-cancel"
            className="px-4 py-2 rounded-2xl text-sm text-white/80 hover:bg-white/5 min-h-[44px]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="table-editor-submit"
            className={
              'px-4 py-2 rounded-2xl text-sm font-semibold min-h-[44px] ' +
              (mode === 'delete'
                ? 'bg-red-600/20 text-red-200 border border-red-600/40 hover:bg-red-600/30'
                : 'bg-gold/20 text-gold border border-gold/40 hover:bg-gold/30')
            }
          >
            {busy ? 'Working…'
              : mode === 'create' ? 'Create table'
              : mode === 'edit' ? 'Save changes'
              : 'Confirm delete'}
          </button>
        </div>
      </form>
    </div>
  );
}
